import React from 'react';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';

interface GridLayoutProps {
  data: [string, any][];
  getFieldLabel: (key: string) => string;
  formatValue: (value: any) => React.ReactNode;
}

/**
 * Grid layout para imágenes y videos
 * Muestra los elementos en una cuadrícula 2x2
 */
export const ImageGridLayout: React.FC<GridLayoutProps> = ({ data, getFieldLabel, formatValue }) => {
  const { fontSize } = useResponsiveSizes();

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 12
      }}
    >
      {data.map(([key, value]) => (
        <div
          key={key}
          style={{
            padding: 12,
            background: 'white',
            borderRadius: 6,
            border: '1px solid #e2e8f0',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: fontSize.sm, fontWeight: 500, marginBottom: 8, color: '#64748b' }}>
            {getFieldLabel(key)}
          </div>
          {formatValue(value)}
        </div>
      ))}
    </div>
  );
};

/**
 * Grid layout para regiones y horarios
 * Agrupa automáticamente region1/time1, region2/time2, etc.
 */
export const RegionGridLayout: React.FC<GridLayoutProps> = ({ data, getFieldLabel, formatValue }) => {
  const { fontSize, spacing } = useResponsiveSizes();

  // Agrupar regiones y tiempos por número
  const regionMap = new Map<string, Record<string, any>>();

  data.forEach(([key, value]) => {
    const match = key.match(/region(\d+)|time(\d+)/);
    if (match) {
      const num = match[1] || match[2];
      if (!regionMap.has(num)) {
        regionMap.set(num, {});
      }
      const regionData = regionMap.get(num)!;
      regionData[key] = value;
    }
  });

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: spacing.sm
      }}
    >
      {Array.from(regionMap.entries()).map(([num, fields]) => (
        <div
          key={num}
          style={{
            padding: spacing.sm,
            background: 'white',
            borderRadius: 6,
            border: '1px solid #e2e8f0'
          }}
        >
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-around',
              fontSize: fontSize.base,
              fontWeight: 500
            }}
          >
            {fields[`region${num}`] && <div>📍 {fields[`region${num}`]}</div>}
            {fields[`time${num}`] && <div>🕐 {fields[`time${num}`]}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Detecta si los datos son apropiados para ImageGridLayout
 */
export const isImageGrid = (data: [string, any][]): boolean => {
  return data.every(([key]) => key.includes('image') || key.includes('video'));
};
